import { useEffect, useState } from 'react'
import { ArrowLeft, BookOpen, Camera, ExternalLink, FileText } from 'lucide-react'
import { useNavigate } from 'react-router'
import { api } from '../lib/api'
import { useAuthed } from '../lib/session'
import { Masthead } from '../ui/Chrome'
import { Button } from '../ui/Button'
import { Skeleton } from '../ui/Feedback'

type FeatureKind = 'gallery' | 'resources'

export function FeatureLandingRoute({ kind }: { kind: FeatureKind }) {
  const navigate = useNavigate()
  const session = useAuthed()
  const gallery = kind === 'gallery'
  const title = gallery ? 'Gallery' : 'Resources'
  const kicker = gallery ? 'Event memories' : 'Event materials'
  const Icon = gallery ? Camera : BookOpen

  /*
   * The destination is per-event and organisers set it from the Settings screen, so it
   * has to be fetched rather than built in - photos and slides usually appear on day
   * two, and a link that needs a redeploy to change is one that stays wrong.
   *
   * `undefined` is "still asking", `null` is "asked, and there is nothing set". Those
   * are different screens, so they cannot share a value.
   */
  const [url, setUrl] = useState<string | null | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    api
      .getSettings(session.email, session.at)
      .then((settings) => {
        if (cancelled) return
        setUrl((gallery ? settings.gallery_url : settings.resources_url) || null)
      })
      // A settings call that fails is indistinguishable, from here, from a link that was
      // never set - and "coming soon" is the safer of the two things to say.
      .catch(() => { if (!cancelled) setUrl(null) })
    return () => { cancelled = true }
  }, [gallery, session.at, session.email])

  const waiting = gallery
    ? 'Event photos and shared moments will appear here once the gallery is published.'
    : 'Programme files, presentation slides, handouts and useful links will appear here once resources are published.'

  /* The masthead already says "Gallery", so the heading must not say it again. */
  const readyTitle = gallery ? 'The photos are published' : 'The materials are published'
  const ready = gallery
    ? 'A shared album, hosted outside the app. It opens in a new tab.'
    : 'Slides, handouts and the programme file, hosted outside the app. Opens in a new tab.'

  return (
    <main className="page feature-page">
      <Masthead
        kicker={kicker}
        title={title}
        action={
          <button className="icon-button" type="button" onClick={() => navigate('/')} aria-label="Back to home">
            <ArrowLeft size={19} strokeWidth={1.5} />
          </button>
        }
      />

      <section className="feature-empty">
        <div className="feature-empty__icon"><Icon size={26} strokeWidth={1.4} /></div>

        {url === undefined ? (
          <Skeleton height="6rem" />
        ) : (
          <>
            <h2 className="display feature-empty__title">{url ? readyTitle : 'Coming soon'}</h2>
            <p className="feature-empty__body">{url ? ready : waiting}</p>
            {url ? (
              /*
               * A real anchor, not a button that navigates: it is an external address,
               * so it should behave like a link - long-press, copy, open in background.
               * noopener because the destination is a URL an organiser typed.
               */
              <a
                className="btn btn--primary feature-empty__open"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink size={17} strokeWidth={1.5} />
                Open {title.toLowerCase()}
              </a>
            ) : (
              <Button variant="outline" icon={<ArrowLeft size={16} strokeWidth={1.5} />} onClick={() => navigate('/')}>
                Back to home
              </Button>
            )}
          </>
        )}
      </section>

      {url === null ? (
        <div className="feature-note">
          <FileText size={15} strokeWidth={1.4} />
          <span>The organisers add this link from their settings screen; it will appear here as soon as they do.</span>
        </div>
      ) : null}
    </main>
  )
}
